<template>
  <div class="admin-layout">
    <!-- 顶部导航栏 -->
    <div class="topbar" v-fade-in>
      <div class="topbar-brand">后台管理</div>
      <nav class="topbar-nav">
        <div
          v-for="(item, index) in menuItems"
          :key="item.id"
          :class="['nav-item', { active: currentTab === item.id }]"
          @click="selectMenu(item.id)"
          v-motion="{ initial: { opacity: 0, y: -12 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.25, delay: index * 0.03 } }"
        >
          <component :is="item.icon" class="nav-icon" />
          <span class="nav-label">{{ item.label }}</span>
        </div>
      </nav>
      <div class="topbar-right">
        <div class="avatar">A</div>
        <button class="logout-btn" @click="handleLogout">
          <LogOut :size="14" />
          <span>退出</span>
        </button>
      </div>
      <button class="menu-toggle" @click="sidebarOpen = !sidebarOpen">
        <Menu :size="22" />
      </button>
    </div>

    <!-- 移动端下拉菜单 -->
    <div class="mobile-overlay" v-if="sidebarOpen" @click="sidebarOpen = false"></div>
    <div :class="['mobile-menu', { open: sidebarOpen }]">
      <div
        v-for="item in menuItems"
        :key="item.id"
        :class="['mobile-menu-item', { active: currentTab === item.id }]"
        @click="selectMenu(item.id)"
      >
        <component :is="item.icon" class="nav-icon" />
        <span>{{ item.label }}</span>
      </div>
      <div class="mobile-menu-footer">
        <button class="logout-btn" @click="handleLogout">
          <LogOut :size="14" />
          <span>退出登录</span>
        </button>
      </div>
    </div>

    <!-- 主内容区 -->
    <div class="main-content">
      <div class="content-body">
        <keep-alive>
          <component :is="currentComponent" />
        </keep-alive>
      </div>
    </div>
  </div>
</template>

<script>
import UserManagement from './components/UserManagement.vue'
import InterfaceManagement from './components/InterfaceManagement.vue'
import PasswordManagement from './components/PasswordManagement.vue'
import SystemSettings from './components/SystemSettings.vue'
import OrderManagement from './components/OrderManagement.vue'
import CustomAntiRedManagement from './components/CustomAntiRedManagement.vue'
import SystemMonitoring from './components/SystemMonitoring.vue'
import LicenseManagement from './components/LicenseManagement.vue'
import AnnouncementManagement from './components/AnnouncementManagement.vue'
import RedEnvelopeManagement from './components/RedEnvelopeManagement.vue'
import { Users, Plug, Key, LogOut, Menu, Settings, FileText, Shield, Activity, Award, Megaphone, Gift } from 'lucide-vue-next'
import { chatWebSocket } from '@/utils/websocket'

export default {
  name: 'AdminPage',
  components: {
    UserManagement,
    InterfaceManagement,
    PasswordManagement,
    SystemSettings,
    OrderManagement,
    CustomAntiRedManagement,
    SystemMonitoring,
    LicenseManagement,
    AnnouncementManagement,
    RedEnvelopeManagement,
    Users,
    Plug,
    Key,
    LogOut,
    Menu,
    Settings,
    FileText,
    Shield,
    Activity,
    Award,
    Megaphone,
    Gift
  },
  data() {
    return {
      currentTab: 'user',
      sidebarOpen: false,
      menuItems: [
        { id: 'monitoring', label: '系统监控', icon: 'Activity', component: 'SystemMonitoring' },
        { id: 'user', label: '用户管理', icon: 'Users', component: 'UserManagement' },
        { id: 'interface', label: '接口管理', icon: 'Plug', component: 'InterfaceManagement' },
        { id: 'order', label: '订单管理', icon: 'FileText', component: 'OrderManagement' },
        { id: 'antired', label: '自定义防红', icon: 'Shield', component: 'CustomAntiRedManagement' },
        { id: 'license', label: '授权管理', icon: 'Award', component: 'LicenseManagement' },
        { id: 'announcement', label: '公告管理', icon: 'Megaphone', component: 'AnnouncementManagement' },
        { id: 'redenvelope', label: '红包管理', icon: 'Gift', component: 'RedEnvelopeManagement' },
        { id: 'settings', label: '系统设置', icon: 'Settings', component: 'SystemSettings' },
        { id: 'password', label: '修改密码', icon: 'Key', component: 'PasswordManagement' }
      ]
    }
  },
  computed: {
    currentComponent() {
      const item = this.menuItems.find(i => i.id === this.currentTab)
      return item ? item.component : null
    },
    currentTitle() {
      const item = this.menuItems.find(i => i.id === this.currentTab)
      return item ? item.label : ''
    }
  },
  mounted() {
    // 管理后台不自动连接 WebSocket，只在需要时（如查看系统监控）才连接
    // 这样可以大幅减少后端资源占用
    console.log('管理后台已加载，WebSocket 将在需要时自动连接')
  },
  beforeUnmount() {
    // 退出时断开 WebSocket 连接
    this.disconnectWebSocket()
  },
  methods: {
    selectMenu(id) {
      this.currentTab = id
      this.sidebarOpen = false
    },
    handleLogout() {
      if (confirm('确定退出登录吗？')) {
        // 断开 WebSocket 连接
        this.disconnectWebSocket()

        localStorage.removeItem('user')
        localStorage.removeItem('token')
        localStorage.removeItem('username')
        // 使用 replace 强制刷新并跳转到登录页
        window.location.replace('/#/login')
        // 延迟一下再刷新，确保跳转完成
        setTimeout(() => {
          window.location.reload()
        }, 100)
      }
    },
    async connectWebSocket() {
      try {
        // 获取当前登录的用户账号
        const username = localStorage.getItem('username') || 'admin'

        // 初始化全局 WebSocket 连接
        await chatWebSocket.init(username)

        console.log('管理员 WebSocket 连接成功:', username)

        // 监听消息（可选：用于显示通知）
        chatWebSocket.onMessage((data) => {
          console.log('管理员收到 WebSocket 消息:', data)
          // 这里可以添加消息通知逻辑，比如显示桌面通知或更新未读消息数
        })
      } catch (error) {
        console.error('管理员 WebSocket 连接失败:', error)
        // 连接失败不影响其他功能，静默处理
      }
    },
    disconnectWebSocket() {
      try {
        chatWebSocket.close()
        console.log('管理员 WebSocket 已断开')
      } catch (error) {
        console.error('断开 WebSocket 连接失败:', error)
      }
    }
  }
}
</script>

<style scoped>
.admin-layout {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: #f5f5f5;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

/* 顶部导航栏 */
.topbar {
  height: 56px;
  background: #fff;
  display: flex;
  align-items: center;
  padding: 0 24px;
  border-bottom: 1px solid #e8e8e8;
  flex-shrink: 0;
  z-index: 100;
}

.topbar-brand {
  font-size: 15px;
  font-weight: 600;
  color: #111;
  letter-spacing: 2px;
  margin-right: 32px;
  flex-shrink: 0;
}

.topbar-nav {
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
  overflow-x: auto;
}

.topbar-nav::-webkit-scrollbar {
  display: none;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  cursor: pointer;
  color: #666;
  border-radius: 4px;
  transition: background 0.15s, color 0.15s;
  white-space: nowrap;
  flex-shrink: 0;
}

.nav-item:hover {
  background: #f5f5f5;
  color: #333;
}

.nav-item.active {
  background: #111;
  color: #fff;
}

.nav-icon {
  width: 16px;
  height: 16px;
  opacity: 0.7;
}

.nav-item.active .nav-icon {
  opacity: 1;
}

.nav-label {
  font-size: 13px;
  font-weight: 400;
}

.topbar-right {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: 24px;
  flex-shrink: 0;
}

.avatar {
  width: 32px;
  height: 32px;
  background: #111;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 500;
  color: #fff;
}

.logout-btn {
  padding: 6px 12px;
  background: none;
  border: 1px solid #ddd;
  color: #999;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.15s;
}

.logout-btn:hover {
  border-color: #111;
  color: #111;
}

/* 汉堡菜单按钮 - 仅移动端 */
.menu-toggle {
  display: none;
  background: none;
  border: 1px solid #ddd;
  padding: 6px;
  cursor: pointer;
  color: #333;
  border-radius: 4px;
  margin-left: auto;
  flex-shrink: 0;
}

.menu-toggle:hover {
  border-color: #111;
}

/* 移动端下拉菜单 */
.mobile-overlay {
  display: none;
}

.mobile-menu {
  display: none;
}

/* 主内容区 */
.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
}

.content-body {
  flex: 1;
  padding: 24px 32px;
  overflow-y: auto;
  background: #f5f5f5;
}

.content-body::-webkit-scrollbar {
  width: 6px;
}

.content-body::-webkit-scrollbar-track {
  background: transparent;
}

.content-body::-webkit-scrollbar-thumb {
  background: #ddd;
  border-radius: 3px;
}

.content-body::-webkit-scrollbar-thumb:hover {
  background: #bbb;
}

/* 移动端适配 */
@media screen and (max-width: 768px) {
  .topbar {
    height: 48px;
    padding: 0 16px;
  }

  .topbar-nav {
    display: none;
  }

  .topbar-right {
    display: none;
  }

  .menu-toggle {
    display: flex;
  }

  .mobile-overlay {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: 998;
  }

  .mobile-menu {
    display: block;
    position: fixed;
    top: 48px;
    left: 0;
    right: 0;
    background: #fff;
    border-bottom: 1px solid #e8e8e8;
    z-index: 999;
    padding: 8px 0;
    transform: translateY(-100%);
    opacity: 0;
    pointer-events: none;
    transition: transform 0.2s ease, opacity 0.2s ease;
  }

  .mobile-menu.open {
    transform: translateY(0);
    opacity: 1;
    pointer-events: auto;
  }

  .mobile-menu-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 20px;
    color: #666;
    cursor: pointer;
    font-size: 14px;
    transition: background 0.15s;
  }

  .mobile-menu-item:hover {
    background: #f5f5f5;
  }

  .mobile-menu-item.active {
    background: #111;
    color: #fff;
  }

  .mobile-menu-item .nav-icon {
    width: 18px;
    height: 18px;
  }

  .mobile-menu-footer {
    padding: 12px 20px;
    border-top: 1px solid #e8e8e8;
    margin-top: 4px;
  }

  .content-body {
    padding: 16px;
  }
}
</style>
